import React from "react";
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Download,
  Visibility,
  VerifiedUser,
  Security,
  CheckCircle,
  Description,
  CalendarToday,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

type CertificateItem = {
  id: number;
  titleKey: string;
  organizationKey: string;
  descriptionKey: string;
  categoryKey: string;
  statusKey: string;
  documentNo: string;
  issueDate: string;
  validUntil: string;
  fileSize: string;
  preview?: string;
  file?: string;
};

const certificationStats = (t: any, i18n: any) => [
  { label: t("certificates.stats.active"), value: "6" },
  { label: t("certificates.stats.compliance"), value: "%100" },
  { label: t("certificates.stats.lastAudit"), value: "2024" },
  {
    label: t("certificates.stats.validity"),
    value: i18n.language.startsWith("tr") ? "2 Yıl" : "2 Years",
  },
];

const certificationBenefits = (t: any) =>
  (t("certificates.benefits.items", { returnObjects: true }) as string[]) || [];

const Certificates: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [filter, setFilter] = React.useState<string>("all");
  const [items, setItems] = React.useState<CertificateItem[]>([]);
  React.useEffect(() => {
    fetch("/data/certificates/index.json", { cache: "no-store" })
      .then((r) => r.json())
      .then((json) => setItems(Array.isArray(json.items) ? json.items : []))
      .catch(() => setItems([]));
  }, []);

  const categoryKeys = [
    "all",
    "certificates.categories.fireSafety",
    "certificates.categories.qualityManagement",
    "certificates.categories.europeanStandard",
    "certificates.categories.testStandard",
    "certificates.categories.qualityAssurance",
    "certificates.categories.environmentalManagement",
  ];

  const filteredCertificates = items.filter(
    (cert: any) => filter === "all" || cert.categoryKey === filter
  );

  const getStatusColor = (statusKey: string) => {
    return statusKey === "certificates.status.active" ? "success" : "warning";
  };

  return (
    <Box sx={{ bgcolor: "#fafafa" }}>
      {/* Header Section */}
      <Box sx={{ bgcolor: "white", borderBottom: "1px solid #e0e0e0" }}>
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: "#1a1a1a",
                  mb: 2,
                  lineHeight: 1.2,
                }}
              >
                {t("certificates.title").split(" ")[0]}{" "}
                {t("certificates.title").split(" ")[1] || ""}
                <br />
                <Box component="span" sx={{ color: "#d32f2f" }}>
                  {t("certificates.title").split(" ").slice(2).join(" ") ||
                    t("certificates.title")}
                </Box>
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ mb: 4, lineHeight: 1.6, fontWeight: 400 }}
              >
                {t("certificates.subtitle")}
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Chip
                  label={t("certificates.badges.tse")}
                  sx={{ bgcolor: "#f5f5f5", fontWeight: 500 }}
                />
                <Chip
                  label={t("certificates.badges.ce")}
                  sx={{ bgcolor: "#f5f5f5", fontWeight: 500 }}
                />
                <Chip
                  label={t("certificates.badges.iso")}
                  sx={{ bgcolor: "#f5f5f5", fontWeight: 500 }}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 3,
                  bgcolor: "#d32f2f",
                  color: "white",
                  textAlign: "center",
                  borderRadius: 2,
                }}
              >
                <VerifiedUser sx={{ fontSize: 48, mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("certificates.qa.title")}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {t("certificates.qa.short")}
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={3} sx={{ mb: 6 }}>
          {certificationStats(t, i18n).map((stat, index) => (
            <Grid key={index} item xs={6} md={3}>
              <Card
                sx={{
                  textAlign: "center",
                  p: 3,
                  bgcolor: "white",
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  transition: "box-shadow 0.2s ease",
                  "&:hover": {
                    boxShadow: 2,
                  },
                }}
              >
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: "#d32f2f", mb: 1 }}
                >
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Filter Buttons */}
        <Card
          sx={{
            p: 2,
            mb: 4,
            bgcolor: "white",
            border: "1px solid #e0e0e0",
            borderRadius: 2,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems="center"
            justifyContent="center"
          >
            <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
              {t("certificates.filters.category")}
            </Typography>
            {categoryKeys.map((categoryKey) => (
              <Button
                key={categoryKey}
                variant={filter === categoryKey ? "contained" : "outlined"}
                size="small"
                onClick={() => setFilter(categoryKey)}
                sx={{
                  borderRadius: 1,
                  textTransform: "none",
                  ...(filter === categoryKey
                    ? {
                        bgcolor: "#d32f2f",
                        "&:hover": { bgcolor: "#b71c1c" },
                      }
                    : {
                        borderColor: "#d32f2f",
                        color: "#d32f2f",
                        "&:hover": {
                          borderColor: "#b71c1c",
                          bgcolor: "rgba(211, 47, 47, 0.04)",
                        },
                      }),
                }}
              >
                {categoryKey === "all"
                  ? i18n.language.startsWith("tr")
                    ? "Tümü"
                    : "All"
                  : t(categoryKey)}
              </Button>
            ))}
          </Stack>
        </Card>

        <Grid container spacing={4}>
          {/* Certificates List */}
          <Grid item xs={12} md={8}>
            <Stack spacing={3}>
              {filteredCertificates.map((cert) => (
                <Card
                  key={cert.id}
                  sx={{
                    bgcolor: "white",
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: "#d32f2f",
                      boxShadow: 2,
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={8}>
                        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                          <Chip
                            label={t(cert.categoryKey as any)}
                            size="small"
                            sx={{ bgcolor: "#f5f5f5" }}
                          />
                          <Chip
                            label={t(cert.statusKey as any)}
                            size="small"
                            color={getStatusColor(cert.statusKey)}
                            variant="outlined"
                          />
                        </Stack>

                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 600, mb: 1, color: "#d32f2f" }}
                        >
                          {t(cert.titleKey as any)}
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2, fontWeight: 500 }}
                        >
                          {t(cert.organizationKey as any)}
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2, lineHeight: 1.6 }}
                        >
                          {t(cert.descriptionKey as any)}
                        </Typography>

                        <Stack spacing={1}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Description fontSize="small" color="action" />
                            <Typography variant="caption">
                              {t("common.documentNoShort")}: {cert.documentNo}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <CalendarToday fontSize="small" color="action" />
                            <Typography variant="caption">
                              {t("common.validUntil")}:{" "}
                              {new Date(cert.validUntil).toLocaleDateString(
                                i18n.language
                              )}
                            </Typography>
                          </Box>
                        </Stack>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: { xs: "left", md: "right" } }}>
                          <Typography variant="caption" color="text.secondary">
                            {t("certificates.list.fileSize")} {cert.fileSize}
                          </Typography>
                          <Stack
                            direction={{ xs: "row", md: "column" }}
                            spacing={1}
                            sx={{ mt: 2 }}
                          >
                            {Boolean(cert.preview || cert.file) && (
                              <Button
                                component="a"
                                href={(cert.preview || cert.file) as string}
                                target="_blank"
                                variant="outlined"
                                size="small"
                                startIcon={<Visibility />}
                                sx={{
                                  borderColor: "#d32f2f",
                                  color: "#d32f2f",
                                  "&:hover": {
                                    borderColor: "#b71c1c",
                                    bgcolor: "rgba(211, 47, 47, 0.04)",
                                  },
                                  borderRadius: 1,
                                }}
                              >
                                {t("common.view")}
                              </Button>
                            )}
                            {cert.file && (
                              <Button
                                component="a"
                                href={cert.file}
                                target="_blank"
                                variant="contained"
                                size="small"
                                startIcon={<Download />}
                                sx={{
                                  bgcolor: "#d32f2f",
                                  "&:hover": { bgcolor: "#b71c1c" },
                                  borderRadius: 1,
                                }}
                              >
                                {t("common.download")}
                              </Button>
                            )}
                          </Stack>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              {/* Certification Benefits */}
              <Card
                sx={{
                  p: 3,
                  bgcolor: "white",
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  {t("certificates.benefits.title")}
                </Typography>
                <List disablePadding>
                  {certificationBenefits(t).map((benefit, index) => (
                    <ListItem key={index} disableGutters>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircle sx={{ color: "#4caf50", fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={benefit}
                        primaryTypographyProps={{ variant: "body2" }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Card>

              {/* Quality Assurance */}
              <Card
                sx={{
                  p: 3,
                  bgcolor: "#f8f9fa",
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Security sx={{ color: "#d32f2f", mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t("certificates.qa.title")}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {t("certificates.qa.text")}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="text.secondary">
                  {t("certificates.qa.lastUpdate", {
                    date: new Date().toLocaleDateString(i18n.language),
                  })}
                </Typography>
              </Card>

              {/* Contact for More Info */}
              <Card
                sx={{
                  p: 3,
                  bgcolor: "white",
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  {t("certificates.more.title")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {t("certificates.more.text")}
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    bgcolor: "#d32f2f",
                    "&:hover": { bgcolor: "#b71c1c" },
                    borderRadius: 1,
                  }}
                  href="/contact"
                >
                  {t("certificates.more.cta")}
                </Button>
              </Card>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Certificates;
